import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FiAlertCircle,
  FiBookOpen,
  FiCamera,
  FiChevronLeft,
  FiEdit3,
  FiHome,
  FiImage,
  FiLoader,
  FiFileText,
  FiTrash2,
  FiZap,
} from 'react-icons/fi';
import { assessEssay } from '../services/essayApi';
import {
  createEssayAttachmentValue,
  ESSAY_ATTACHMENT_ACCEPT,
  ESSAY_ATTACHMENTS_TOTAL_MAX_BYTES,
  ESSAY_IMAGE_ACCEPT,
  formatAttachmentSize,
  prepareEssayAttachment,
  releaseEssayAttachmentValue,
} from '../utils/imageUtils';
import EssayResult from './EssayResult';

const gradeOptions = [
  { value: 'elementary', label: '초등' },
  { value: 'middle', label: '중등' },
  { value: 'high', label: '고등' },
  { value: 'college', label: '대학 이상' },
  { value: 'all', label: '학년 미지정' },
];
const GRADE_STORAGE_KEY = 'ai-essay-grade-level';
const DEFAULT_GRADE_LEVEL = 'high';

function isValidGradeLevel(value) {
  return gradeOptions.some((option) => option.value === value);
}

function restoreGradeLevel() {
  try {
    const savedGradeLevel = window.localStorage.getItem(GRADE_STORAGE_KEY);
    return isValidGradeLevel(savedGradeLevel) ? savedGradeLevel : DEFAULT_GRADE_LEVEL;
  } catch (error) {
    return DEFAULT_GRADE_LEVEL;
  }
}

const assessmentOptions = [
  {
    value: 'constructed',
    label: '서술형 답안',
    description: '핵심 개념, 문제 조건, 답의 정확성과 풀이 과정을 확인합니다.',
  },
  {
    value: 'essay',
    label: '논술 답안',
    description: '주장, 근거, 글의 구성과 표현을 종합적으로 확인합니다.',
  },
];

const clipboardImageExtensions = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
};

function createClipboardImageFile(blob, kind) {
  const extension = clipboardImageExtensions[blob.type] || 'png';
  const capturedAt = new Date().toISOString().replace(/\D/g, '');
  return new File([blob], `${kind}-capture-${capturedAt}.${extension}`, {
    type: blob.type,
    lastModified: Date.now(),
  });
}

function getPastedImageFile(clipboardData, kind) {
  const imageItems = Array.from(clipboardData?.items || []).filter(
    (item) => item.kind === 'file' && item.type.startsWith('image/')
  );
  const imageFiles = imageItems.length
    ? imageItems.map((item) => item.getAsFile()).filter(Boolean)
    : Array.from(clipboardData?.files || []).filter((file) => file.type.startsWith('image/'));
  if (imageFiles.length > 1) throw new Error('클립보드 이미지는 한 번에 한 장만 붙여넣을 수 있습니다.');
  const [imageFile] = imageFiles;
  return imageFile ? createClipboardImageFile(imageFile, kind) : null;
}

function AttachmentPreview({ kindLabel, value, onRemove, disabled }) {
  if (!value) return null;
  const isPdf = value.kind === 'pdf';
  return (
    <div className={`ai-essay-image-preview${isPdf ? ' is-pdf' : ''}`}>
      <span className="ai-essay-sr-only" role="status" aria-live="polite">{kindLabel} 첨부 완료: {value.name}</span>
      {isPdf
        ? <span className="ai-essay-image-preview__pdf" aria-hidden="true"><FiFileText /></span>
        : <img src={value.previewUrl} alt={`${kindLabel} 이미지 미리보기`} />}
      <div>
        <strong>{value.name}</strong>
        <span>{isPdf ? 'PDF 문서 · ' : ''}{formatAttachmentSize(value.size)}</span>
      </div>
      <button type="button" onClick={onRemove} disabled={disabled} aria-label={`${kindLabel} 첨부 파일 삭제`}><FiTrash2 aria-hidden="true" /></button>
    </div>
  );
}

function SourceInput({
  kind,
  title,
  description,
  text,
  attachment,
  textRef,
  cameraRef,
  galleryRef,
  onTextChange,
  onFileChange,
  onClipboardPaste,
  onRemoveAttachment,
  disabled,
  required,
}) {
  return (
    <section
      className="ai-essay-source-card"
      role="group"
      aria-labelledby={`${kind}-source-title`}
      aria-describedby={`${kind}-source-description`}
      onPaste={(event) => { if (!disabled) onClipboardPaste(kind, event); }}
    >
      <div className="ai-essay-source-card__heading">
        <div><h3 id={`${kind}-source-title`}>{title}</h3></div>
        <small>{required ? '필수' : '선택'}</small>
      </div>
      <p id={`${kind}-source-description`} className="ai-essay-source-card__description">{description}</p>

      <div className="ai-essay-source-actions" aria-label={`${title} 입력 방법`}>
        <button type="button" onClick={() => cameraRef.current?.click()} disabled={disabled}>
          <FiCamera aria-hidden="true" />사진 촬영
        </button>
        <button type="button" onClick={() => galleryRef.current?.click()} disabled={disabled}>
          <FiImage aria-hidden="true" />이미지·PDF 선택
        </button>
        <button type="button" onClick={() => textRef.current?.focus()} disabled={disabled}>
          <FiEdit3 aria-hidden="true" />직접 입력
        </button>
      </div>

      <input
        ref={cameraRef}
        className="ai-essay-file-input"
        type="file"
        accept={ESSAY_IMAGE_ACCEPT}
        capture="environment"
        onChange={(event) => onFileChange(kind, event)}
        disabled={disabled}
        tabIndex={-1}
        hidden
      />
      <input
        ref={galleryRef}
        className="ai-essay-file-input"
        type="file"
        accept={ESSAY_ATTACHMENT_ACCEPT}
        onChange={(event) => onFileChange(kind, event)}
        disabled={disabled}
        tabIndex={-1}
        hidden
      />

      <AttachmentPreview
        kindLabel={kind === 'problem' ? '문제' : '학생 답안'}
        value={attachment}
        onRemove={onRemoveAttachment}
        disabled={disabled}
      />

      <label className="ai-essay-text-field">
        <span>{kind === 'problem' ? '문제 내용' : '학생 답안'}</span>
        <textarea
          ref={textRef}
          value={text}
          onChange={(event) => onTextChange(event.target.value)}
          placeholder={kind === 'problem'
            ? '문제를 직접 입력하거나 사진·이미지·PDF를 첨부하세요. 캡처 이미지는 Ctrl+V로 붙여넣을 수 있습니다.'
            : '학생 답안을 직접 입력하거나 사진·이미지·PDF를 첨부하세요. 캡처 이미지는 Ctrl+V로 붙여넣을 수 있습니다.'}
          maxLength={kind === 'problem' ? 8000 : 16000}
          rows={kind === 'problem' ? 5 : 8}
          disabled={disabled}
          aria-keyshortcuts="Control+V Meta+V"
        />
        <small>{text.length.toLocaleString()} / {(kind === 'problem' ? 8000 : 16000).toLocaleString()}자</small>
      </label>
    </section>
  );
}

function EssayWorkspace({ subject, onBack, onHome }) {
  const SubjectIcon = subject.icon;
  const [taskType, setTaskType] = useState('diagnose');
  const [assessmentType, setAssessmentType] = useState('constructed');
  const [gradeLevel, setGradeLevel] = useState(restoreGradeLevel);
  const [problemText, setProblemText] = useState('');
  const [answerText, setAnswerText] = useState('');
  const [rubricText, setRubricText] = useState('');
  const [problemAttachment, setProblemAttachment] = useState(null);
  const [answerAttachment, setAnswerAttachment] = useState(null);
  const [status, setStatus] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [errorRequestId, setErrorRequestId] = useState('');
  const [requestToast, setRequestToast] = useState(null);
  const [resultPayload, setResultPayload] = useState(null);

  const problemCameraRef = useRef(null);
  const problemGalleryRef = useRef(null);
  const answerCameraRef = useRef(null);
  const answerGalleryRef = useRef(null);
  const problemTextRef = useRef(null);
  const answerTextRef = useRef(null);
  const requestControllerRef = useRef(null);
  const errorRef = useRef(null);
  const mountedRef = useRef(true);
  const attachmentRequestRef = useRef({ problem: 0, answer: 0 });

  const isBusy = status === 'preparing' || status === 'analyzing';
  const hasProblem = Boolean(problemText.trim() || problemAttachment);
  const hasAnswer = Boolean(answerText.trim() || answerAttachment);
  const canSubmit = hasProblem && (taskType === 'solve' || hasAnswer) && !isBusy;
  const assessment = assessmentOptions.find((option) => option.value === assessmentType) || assessmentOptions[0];
  const assessmentName = assessmentType === 'essay' ? '논술' : '서술형';

  useEffect(() => () => releaseEssayAttachmentValue(problemAttachment), [problemAttachment]);
  useEffect(() => () => releaseEssayAttachmentValue(answerAttachment), [answerAttachment]);
  useEffect(() => {
    try {
      window.localStorage.setItem(GRADE_STORAGE_KEY, gradeLevel);
    } catch (error) {
      // 저장소를 사용할 수 없는 환경에서도 현재 화면 선택은 유지한다.
    }
  }, [gradeLevel]);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (resultPayload?.result) document.querySelector('.ai-essay-page')?.scrollTo({ top: 0, behavior: 'auto' });
  }, [resultPayload?.result]);

  useEffect(() => {
    if (!error) return;
    window.requestAnimationFrame(() => {
      errorRef.current?.focus({ preventScroll: true });
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [error]);

  useEffect(() => {
    if (!requestToast) return undefined;
    const toastTimer = window.setTimeout(() => setRequestToast(null), 8000);
    return () => window.clearTimeout(toastTimer);
  }, [requestToast]);

  const requirementText = useMemo(() => {
    if (!hasProblem) return '문제 내용 또는 문제 첨부 파일이 필요합니다.';
    if (taskType === 'diagnose' && !hasAnswer) return `${assessmentName} 진단에는 학생 답안 또는 답안 첨부 파일이 필요합니다.`;
    return taskType === 'solve'
      ? '문제를 AI가 단계별로 풀이합니다.'
      : `입력한 ${assessmentName} 답안을 AI가 전용 기준으로 진단하고 첨삭합니다.`;
  }, [assessmentName, hasAnswer, hasProblem, taskType]);

  const prepareAndSetAttachment = async (kind, fileSource) => {
    const attachmentRequestId = attachmentRequestRef.current[kind] + 1;
    attachmentRequestRef.current[kind] = attachmentRequestId;
    setStatus('preparing');
    setError('');
    setErrorRequestId('');
    setRequestToast(null);
    try {
      const file = typeof fileSource === 'function' ? await fileSource() : fileSource;
      const preparedFile = await prepareEssayAttachment(file);
      const otherAttachmentSize = kind === 'problem'
        ? (taskType === 'diagnose' ? answerAttachment?.size || 0 : 0)
        : problemAttachment?.size || 0;
      if (preparedFile.size + otherAttachmentSize > ESSAY_ATTACHMENTS_TOTAL_MAX_BYTES) {
        throw new Error('문제와 답안 첨부 파일의 합계는 10MB 이하여야 합니다. 파일을 줄이거나 한쪽을 직접 입력해 주세요.');
      }
      const attachmentValue = createEssayAttachmentValue(preparedFile);
      if (!mountedRef.current || attachmentRequestRef.current[kind] !== attachmentRequestId) {
        releaseEssayAttachmentValue(attachmentValue);
        return;
      }
      if (kind === 'problem') setProblemAttachment(attachmentValue);
      else setAnswerAttachment(attachmentValue);
    } catch (attachmentError) {
      if (mountedRef.current && attachmentRequestRef.current[kind] === attachmentRequestId) {
        setError(attachmentError?.message || '첨부 파일을 처리하지 못했습니다.');
      }
    } finally {
      if (mountedRef.current && attachmentRequestRef.current[kind] === attachmentRequestId) setStatus('idle');
    }
  };

  const handleFileChange = (kind, event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = '';
    if (file) prepareAndSetAttachment(kind, file);
  };

  const handleClipboardPaste = (kind, event) => {
    let file;
    try {
      file = getPastedImageFile(event.clipboardData, kind);
    } catch (clipboardError) {
      event.preventDefault();
      setError(clipboardError?.message || '클립보드 이미지를 처리하지 못했습니다.');
      setErrorRequestId('');
      setRequestToast(null);
      return;
    }
    if (!file) return;
    event.preventDefault();
    prepareAndSetAttachment(kind, file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isBusy) return;
    setRequestToast(null);
    if (!hasProblem) {
      setError('문제 내용이나 문제 첨부 파일을 먼저 입력해 주세요.');
      setErrorRequestId('');
      return;
    }
    if (taskType === 'diagnose' && !hasAnswer) {
      setError(`진단할 학생 ${assessmentName} 답안이나 답안 첨부 파일을 입력해 주세요.`);
      setErrorRequestId('');
      return;
    }

    const totalAttachmentBytes = (problemAttachment?.size || 0) + (
      taskType === 'diagnose' ? answerAttachment?.size || 0 : 0
    );
    if (totalAttachmentBytes > ESSAY_ATTACHMENTS_TOTAL_MAX_BYTES) {
      setError('문제와 답안 첨부 파일의 합계는 10MB 이하여야 합니다. 파일을 줄이거나 한쪽을 직접 입력해 주세요.');
      setErrorRequestId('');
      return;
    }

    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    setStatus('analyzing');
    setUploadProgress(0);
    setError('');
    setErrorRequestId('');
    setRequestToast(null);

    try {
      const response = await assessEssay({
        subject: subject.id,
        taskType,
        assessmentType,
        gradeLevel,
        problemText: problemText.trim(),
        answerText: taskType === 'diagnose' ? answerText.trim() : '',
        rubricText: taskType === 'diagnose' ? rubricText.trim() : '',
        problemAttachment,
        answerAttachment: taskType === 'diagnose' ? answerAttachment : null,
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          setUploadProgress(Math.min(100, Math.round((progressEvent.loaded / progressEvent.total) * 100)));
        },
      });
      setResultPayload({
        ...response,
        usedAttachment: Boolean(problemAttachment || (taskType === 'diagnose' && answerAttachment)),
      });
      setStatus('success');
    } catch (requestError) {
      if (requestError?.cause?.code === 'ERR_CANCELED') {
        if (mountedRef.current && requestControllerRef.current === controller) setStatus('idle');
        return;
      }
      const requestErrorMessage = requestError?.message || 'AI 분석에 실패했습니다.';
      const requestErrorId = requestError?.requestId || '';
      setError(requestErrorMessage);
      setErrorRequestId(requestErrorId);
      setRequestToast({
        message: requestErrorMessage,
        requestId: requestErrorId,
        model: requestError?.model || '',
      });
      setResultPayload(null);
      setStatus('error');
    } finally {
      if (requestControllerRef.current === controller) requestControllerRef.current = null;
    }
  };

  const resetAll = () => {
    setProblemText('');
    setAnswerText('');
    setRubricText('');
    setProblemAttachment(null);
    setAnswerAttachment(null);
    setResultPayload(null);
    setStatus('idle');
    setError('');
    setErrorRequestId('');
    setRequestToast(null);
    document.querySelector('.ai-essay-page')?.scrollTo({ top: 0, behavior: 'auto' });
  };

  const editInput = () => {
    setResultPayload(null);
    setStatus('idle');
    setError('');
    setErrorRequestId('');
    setRequestToast(null);
    document.querySelector('.ai-essay-page')?.scrollTo({ top: 0, behavior: 'auto' });
  };

  return (
    <div className="ai-essay-detail" style={{ '--essay-accent': subject.color, '--essay-soft': subject.softColor }}>
      {requestToast && typeof document !== 'undefined' && createPortal(
        <div className="ai-essay-request-toast" aria-hidden="true">
          <span className="ai-essay-request-toast__icon"><FiAlertCircle /></span>
          <div>
            <strong>AI 요청에 실패했습니다.</strong>
            <p>{requestToast.message}</p>
            {(requestToast.model || requestToast.requestId) && (
              <small>
                {requestToast.model && <span>모델: {requestToast.model}</span>}
                {requestToast.requestId && <span>문의 코드: {requestToast.requestId}</span>}
              </small>
            )}
          </div>
        </div>,
        document.body
      )}
      <header className="ai-essay-detail-header">
        <button type="button" onClick={onBack} aria-label="과목 선택으로 돌아가기"><FiChevronLeft aria-hidden="true" /></button>
        <h1>{subject.name}</h1>
        <button type="button" onClick={onHome} aria-label="AI 서술형 및 논술 첨삭 홈"><FiHome aria-hidden="true" /></button>
      </header>

      <div className="ai-essay-detail-content">
        {resultPayload?.result ? (
          <EssayResult
            result={resultPayload.result}
            meta={resultPayload}
            subject={subject}
            onEdit={editInput}
            onReset={resetAll}
          />
        ) : (
          <>
            <section className="ai-essay-detail-hero">
              <span className="ai-essay-detail-hero__icon" aria-hidden="true"><SubjectIcon /></span>
              <h2>{taskType === 'solve' ? `${subject.name} 문제 풀이` : `${subject.name} ${assessmentName} 첨삭`}</h2>
              <p>{taskType === 'solve' ? subject.description : assessment.description}</p>
            </section>

            <form className="ai-essay-assessment-form" onSubmit={handleSubmit} aria-busy={isBusy}>
              <section className="ai-essay-mode-panel" aria-labelledby="essay-mode-title">
                <div><h2 id="essay-mode-title">원하는 기능을 선택하세요</h2></div>
                <div className="ai-essay-mode-tabs">
                  <button
                    type="button"
                    className={taskType === 'diagnose' ? 'is-active' : ''}
                    onClick={() => { setTaskType('diagnose'); setError(''); setErrorRequestId(''); setRequestToast(null); }}
                    disabled={isBusy}
                    aria-pressed={taskType === 'diagnose'}
                  >
                    <FiZap aria-hidden="true" /><span><strong>답안 진단·첨삭</strong><small>학생 답안의 점수와 개선 방향</small></span>
                  </button>
                  <button
                    type="button"
                    className={taskType === 'solve' ? 'is-active' : ''}
                    onClick={() => { setTaskType('solve'); setError(''); setErrorRequestId(''); setRequestToast(null); }}
                    disabled={isBusy}
                    aria-pressed={taskType === 'solve'}
                  >
                    <FiBookOpen aria-hidden="true" /><span><strong>문제 풀이</strong><small>사진 속 문제의 단계별 풀이</small></span>
                  </button>
                </div>
                <label className="ai-essay-grade-select">
                  <span>학생 수준</span>
                  <select value={gradeLevel} onChange={(event) => { setGradeLevel(event.target.value); setError(''); setErrorRequestId(''); setRequestToast(null); }} disabled={isBusy}>
                    {gradeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
              </section>

              {taskType === 'diagnose' && (
                <fieldset className="ai-essay-assessment-type" disabled={isBusy}>
                  <legend>첨삭할 답안 유형을 선택하세요</legend>
                  <div className="ai-essay-assessment-type__options">
                    {assessmentOptions.map((option) => (
                      <label key={option.value}>
                        <input
                          type="radio"
                          name="assessmentType"
                          value={option.value}
                          checked={assessmentType === option.value}
                          onChange={() => {
                            setAssessmentType(option.value);
                            setError('');
                            setErrorRequestId('');
                            setRequestToast(null);
                          }}
                        />
                        <span className="ai-essay-assessment-type__card">
                          <span>
                            <strong>{option.label}</strong>
                            <span>{option.description}</span>
                          </span>
                          <span className="ai-essay-assessment-type__indicator" aria-hidden="true" />
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              <SourceInput
                kind="problem"
                title={taskType === 'solve' ? '문제 입력' : `${assessmentName} 문제 입력`}
                description={taskType === 'solve'
                  ? '교재나 시험지의 문제를 촬영하거나 이미지·PDF 또는 텍스트로 입력하세요. PDF는 암호가 없어야 합니다.'
                  : assessmentType === 'constructed'
                    ? '서술형 문항과 지문을 촬영하거나 이미지·PDF 또는 텍스트로 입력하세요. PDF는 암호가 없어야 합니다.'
                    : '논술 문제와 제시문을 촬영하거나 이미지·PDF 또는 텍스트로 입력하세요. PDF는 암호가 없어야 합니다.'}
                text={problemText}
                attachment={problemAttachment}
                textRef={problemTextRef}
                cameraRef={problemCameraRef}
                galleryRef={problemGalleryRef}
                onTextChange={(value) => { setProblemText(value); setError(''); setErrorRequestId(''); setRequestToast(null); }}
                onFileChange={handleFileChange}
                onClipboardPaste={handleClipboardPaste}
                onRemoveAttachment={() => { setProblemAttachment(null); setError(''); setErrorRequestId(''); setRequestToast(null); }}
                disabled={isBusy}
                required
              />

              {taskType === 'diagnose' && (
                <SourceInput
                  kind="answer"
                  title={`학생 ${assessmentName} 답안 입력`}
                  description={assessmentType === 'constructed'
                    ? '학생이 작성한 짧은 답이나 풀이를 촬영하거나 이미지·PDF 또는 텍스트로 입력하세요.'
                    : '학생이 작성한 논술문을 촬영하거나 이미지·PDF 또는 텍스트로 입력하세요.'}
                  text={answerText}
                  attachment={answerAttachment}
                  textRef={answerTextRef}
                  cameraRef={answerCameraRef}
                  galleryRef={answerGalleryRef}
                  onTextChange={(value) => { setAnswerText(value); setError(''); setErrorRequestId(''); setRequestToast(null); }}
                  onFileChange={handleFileChange}
                  onClipboardPaste={handleClipboardPaste}
                  onRemoveAttachment={() => { setAnswerAttachment(null); setError(''); setErrorRequestId(''); setRequestToast(null); }}
                  disabled={isBusy}
                  required
                />
              )}

              {taskType === 'diagnose' && (
                <label className="ai-essay-rubric-field">
                  <span>추가 {assessmentName} 평가 기준 <small>선택</small></span>
                  <textarea
                    value={rubricText}
                    onChange={(event) => { setRubricText(event.target.value); setError(''); setErrorRequestId(''); setRequestToast(null); }}
                    placeholder={assessmentType === 'constructed'
                      ? '필수 정답 요소나 배점 기준이 있다면 입력하세요.'
                      : '논술 채점 기준이나 반드시 확인할 항목이 있다면 입력하세요.'}
                    maxLength={4000}
                    rows="3"
                    disabled={isBusy}
                  />
                </label>
              )}

              {error && (
                <div ref={errorRef} className="ai-essay-form-error" role="alert" tabIndex={-1}>
                  <FiAlertCircle aria-hidden="true" /><div><strong>요청을 완료하지 못했습니다.</strong><p>{error}</p>{errorRequestId && <small>문의 코드: {errorRequestId}</small>}</div>
                </div>
              )}

              {isBusy && (
                <div className="ai-essay-loading" role="status" aria-live="polite">
                  <FiLoader aria-hidden="true" />
                  <div>
                    <strong>{status === 'preparing'
                      ? '첨부 파일을 확인하고 준비하고 있습니다.'
                      : taskType === 'solve'
                        ? 'AI가 문제를 분석하고 풀이하고 있습니다.'
                        : `AI가 ${assessmentName} 전용 기준으로 답안을 분석하고 있습니다.`}</strong>
                    <p>{status === 'analyzing' && uploadProgress > 0 && uploadProgress < 100 ? `첨부 자료 전송 ${uploadProgress}%` : '내용에 따라 최대 1~2분 정도 걸릴 수 있습니다.'}</p>
                  </div>
                </div>
              )}

              <div className="ai-essay-submit-area">
                <p className={canSubmit ? 'is-ready' : ''}>{requirementText}</p>
                <button type="submit" className="ai-essay-start" disabled={!canSubmit}>
                  {status === 'analyzing'
                    ? <><FiLoader className="is-spinning" aria-hidden="true" />AI 분석 중</>
                    : <><FiZap aria-hidden="true" />{taskType === 'solve' ? 'AI 문제 풀이 시작' : `${assessmentName} 진단·첨삭 시작`}</>}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default EssayWorkspace;
